import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaBarcode,
  FaSeedling,
  FaCheckCircle,
  FaCertificate,
  FaAward,
  FaGlobeAmericas,
  FaHandHoldingHeart,
  FaMicroscope,
  FaRecycle,
  FaUsers,
} from "react-icons/fa";

const QualitySustainability = () => {
  const { t } = useTranslation();

  const traceabilitySteps = [
    { icon: <FaBarcode /> },
    { icon: <FaSeedling /> },
    { icon: <FaCheckCircle /> },
    { icon: <FaCertificate /> },
    { icon: <FaAward /> },
    { icon: <FaGlobeAmericas /> },
  ];

  const certificationLogos = [
    "/images/certifications/BRC.webp",
    "/images/certifications/EU.webp",
    "/images/certifications/USDA.webp",
    "/images/certifications/JAS.webp",
    "/images/certifications/Naturland.webp",
    "/images/certifications/FairTrade.webp",
    "/images/certifications/FDA.webp",
  ];

  const impactIcons = [
    <FaBarcode />,
    <FaCertificate />,
    <FaSeedling />,
    <FaHandHoldingHeart />,
    <FaUsers />,
  ];

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Quality & Sustainability | BRC Certified Organic Food Processing with Full Traceability"
        description="End-to-end traceability from farm to shelf. BRC, EU Organic, USDA, JAS, Naturland & Fairtrade certified. HACCP compliant organic food processing. Regenerative agriculture, biodiversity protection, and women's empowerment. Transparent supply chains with batch-level verification."
        keywords="organic food traceability, BRC certified food processing, HACCP organic facility, EU organic certification, USDA organic NOP, JAS organic Japan, Naturland certification, Fairtrade certified organic, regenerative agriculture practices, sustainable food processing, organic quality management, food safety compliance, pesticide residue testing, organic supply chain transparency, biodiversity protection farming, fair trade organic Sri Lanka, batch traceability system, organic certification compliance, sustainable agriculture practices, women empowerment organic farming, soil health restoration, chemical-free food processing, organic food safety management, certified organic processor, traceable organic ingredients, ethical food sourcing, sustainable supply chain management, organic farming standards, food safety culture, environmental impact organic"
        image="https://worganaturals.com/images/quality/brc-certified-facility.jpg"
        url="/quality-sustainability"
        type="website"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Organization Schema - Quality & Certifications Focus */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals - Quality & Sustainability",
            url: "https://worganaturals.com/quality-sustainability",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "BRC certified organic food processor with end-to-end traceability. Multiple international certifications including EU Organic, USDA NOP, JAS, Naturland, and Fairtrade. HACCP compliant facility with regenerative agriculture practices.",
            hasCredential: [
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "BRC Global Standard for Food Safety",
                recognizedBy: {
                  "@type": "Organization",
                  name: "Global Food Safety Initiative (GFSI)",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "EU Organic Certification",
                recognizedBy: {
                  "@type": "Organization",
                  name: "European Union",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "USDA Organic (NOP)",
                recognizedBy: {
                  "@type": "Organization",
                  name: "United States Department of Agriculture",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "JAS Organic Certification",
                recognizedBy: {
                  "@type": "Organization",
                  name: "Japanese Agricultural Standard",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "Naturland Organic",
                recognizedBy: {
                  "@type": "Organization",
                  name: "Naturland International",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "Fairtrade Certification",
                recognizedBy: {
                  "@type": "Organization",
                  name: "Fairtrade International",
                },
              },
              {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "FDA Registration",
                recognizedBy: {
                  "@type": "Organization",
                  name: "U.S. Food and Drug Administration",
                },
              },
            ],
            knowsAbout: [
              "Food Safety Management",
              "HACCP Compliance",
              "Organic Certification",
              "Traceability Systems",
              "Regenerative Agriculture",
              "Quality Assurance",
              "Sustainable Processing",
              "Fair Trade Practices",
            ],
          })}
        </script>

        {/* HowTo Schema - Traceability System */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How Worga Naturals Traceability System Works",
            description:
              "End-to-end visibility from farm to shelf with batch-level verification",
            step: [
              {
                "@type": "HowToStep",
                position: 1,
                name: "Product Label (Traceability Code)",
                text: "Full traceability, tied to each product's batch code",
                image:
                  "https://worganaturals.com/images/traceability/batch-code.jpg",
              },
              {
                "@type": "HowToStep",
                position: 2,
                name: "Organic Farmer",
                text: "Captured field data, farming practices, and harvest timelines",
                image:
                  "https://worganaturals.com/images/traceability/farmer-data.jpg",
              },
              {
                "@type": "HowToStep",
                position: 3,
                name: "Supplier & Raw Materials",
                text: "Source locations, lot numbers, and quality checks",
                image:
                  "https://worganaturals.com/images/traceability/raw-materials.jpg",
              },
              {
                "@type": "HowToStep",
                position: 4,
                name: "Factory (Processor)",
                text: "Processing steps, sanitation records, and CCPs",
                image:
                  "https://worganaturals.com/images/traceability/processing.jpg",
              },
              {
                "@type": "HowToStep",
                position: 5,
                name: "Finished Products",
                text: "Packaging, bottling, labeling compliance, and release",
                image:
                  "https://worganaturals.com/images/traceability/packaging.jpg",
              },
              {
                "@type": "HowToStep",
                position: 6,
                name: "Retailer & Buyer",
                text: "Verification at any point in the chain from farm to shelf",
                image:
                  "https://worganaturals.com/images/traceability/retail.jpg",
              },
            ],
            totalTime: "PT1M",
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What certifications does Worga Naturals hold?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We hold BRC Global Standard for Food Safety, EU Organic Certification, USDA Organic (NOP), JAS Organic Certification, Naturland Organic, Fairtrade Certification, and FDA Registration. These certifications ensure world-class food safety, organic integrity, and fair trade compliance.",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals' traceability system work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Each batch carries a unique traceability code that links finished products back to the farmer, supplier, raw materials, and every processing stage at our facility. This end-to-end visibility verifies origin, farming practices, handling, and compliance. You can verify origin and farm group, certification status, processing records, and storage conditions.",
                },
              },
              {
                "@type": "Question",
                name: "What is BRC certification and why does it matter?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "BRC Global Standard for Food Safety is an internationally recognized GFSI-benchmarked standard ensuring robust food safety and quality management. It covers HACCP, GMP, allergen control, foreign-body prevention, sanitation, traceability, training, and management review. It demonstrates world-class risk management and compliance demanded by global retailers and brands.",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals ensure organic integrity?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We maintain multiple organic certifications (EU, USDA, JAS, Naturland) with prohibition of synthetic pesticides and fertilizers, non-GMO seeds and inputs, soil and biodiversity protection, and audited traceability. We conduct routine residue and contaminant testing, maintain batch-level documentation, and undergo regular third-party audits.",
                },
              },
              {
                "@type": "Question",
                name: "What testing and verification does Worga Naturals perform?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We conduct routine microbiological analysis, shelf-life verification, residue and contaminant testing for pesticides and heavy metals, incoming raw material COA verification, and identity preservation checks. We partner with third-party labs for independent verification.",
                },
              },
              {
                "@type": "Question",
                name: "What is HACCP and how is it implemented?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "HACCP (Hazard Analysis and Critical Control Points) is integrated into all our processing lines. We identify critical control points, monitor them continuously, maintain sanitation protocols, conduct environmental monitoring, and implement validated cleaning protocols with pest control measures.",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals practice regenerative agriculture?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We support soil health through composting, mulching, crop rotations, and organic pest management. We practice water stewardship with rainwater harvesting and efficient irrigation. We protect biodiversity through native species planting, habitat corridors, and pollinator buffers.",
                },
              },
              {
                "@type": "Question",
                name: "What is Fairtrade certification and how does it benefit farmers?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Fairtrade certification ensures fair prices, community premiums, and equitable labor practices. It provides minimum price guarantees, community premium investments, worker protections, and transparent trading relationships. This directly benefits farming communities, funds local projects, and aligns purchasing with social impact.",
                },
              },
              {
                "@type": "Question",
                name: "How can buyers verify product quality and origin?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Every product has a unique batch code that enables verification of origin and farm group, certification status for organic and fair trade, processing records and compliance checkpoints, and storage and transport conditions. This prevents adulteration and mislabeling while enabling rapid recalls if needed.",
                },
              },
              {
                "@type": "Question",
                name: "What is Worga Naturals' food safety culture?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We maintain ongoing training for all staff and field teams, incident reporting and rapid CAPA workflows, open-door audits and continuous coaching, and performance dashboards with leadership accountability. This ensures consistent quality and continuous improvement.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Quality & Sustainability",
                item: "https://worganaturals.com/quality-sustainability",
              },
            ],
          })}
        </script>

        {/* ItemList Schema - Certifications */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Worga Naturals Certifications",
            description:
              "Comprehensive list of international food safety, organic, and fair trade certifications",
            numberOfItems: 7,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Certification",
                  name: "BRC Global Standard for Food Safety",
                  description:
                    "GFSI-benchmarked standard ensuring robust food safety and quality management",
                  issuedBy: {
                    "@type": "Organization",
                    name: "BRC Global Standards",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Certification",
                  name: "EU Organic Certification",
                  description:
                    "Government-recognized organic standard for the European Union",
                  issuedBy: {
                    "@type": "Organization",
                    name: "European Union",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Certification",
                  name: "USDA Organic (NOP)",
                  description:
                    "United States Department of Agriculture organic certification",
                  issuedBy: {
                    "@type": "Organization",
                    name: "USDA",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Certification",
                  name: "JAS Organic Certification",
                  description:
                    "Japanese Agricultural Standard for organic products",
                  issuedBy: {
                    "@type": "Organization",
                    name: "Ministry of Agriculture, Forestry and Fisheries (Japan)",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 5,
                item: {
                  "@type": "Certification",
                  name: "Naturland Organic",
                  description:
                    "International organic standard with social responsibility focus",
                  issuedBy: {
                    "@type": "Organization",
                    name: "Naturland International",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 6,
                item: {
                  "@type": "Certification",
                  name: "Fairtrade Certification",
                  description:
                    "Fair prices, community premiums, and equitable labor practices",
                  issuedBy: {
                    "@type": "Organization",
                    name: "Fairtrade International",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 7,
                item: {
                  "@type": "Certification",
                  name: "FDA Registration",
                  description:
                    "U.S. Food and Drug Administration facility registration",
                  issuedBy: {
                    "@type": "Organization",
                    name: "FDA",
                  },
                },
              },
            ],
          })}
        </script>

        {/* CollectionPage Schema - Quality Management System */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Quality Management System Components",
            description:
              "Comprehensive quality assurance framework ensuring food safety and organic integrity",
            hasPart: [
              {
                "@type": "Thing",
                name: "Documentation & Standards",
                description:
                  "Rigorously documented procedures aligned with BRC Global Standard for Food Safety",
              },
              {
                "@type": "Thing",
                name: "HACCP Integration",
                description:
                  "Hazard Analysis and Critical Control Points integrated into all lines",
              },
              {
                "@type": "Thing",
                name: "GMP Compliance",
                description:
                  "Personnel hygiene, calibrated equipment, allergen and foreign-body control",
              },
              {
                "@type": "Thing",
                name: "Sanitation Protocols",
                description:
                  "Validated cleaning protocols, environmental monitoring, pest control",
              },
              {
                "@type": "Thing",
                name: "Supplier Assurance",
                description:
                  "COAs, audits, and specification compliance for all inputs",
              },
              {
                "@type": "Thing",
                name: "Testing & Analysis",
                description:
                  "Microbiology, moisture, pesticide residue, heavy metals",
              },
              {
                "@type": "Thing",
                name: "Release & Non-Conformance",
                description: "Batch approval, traceable holds, root-cause CAPA",
              },
              {
                "@type": "Thing",
                name: "Continuous Improvement",
                description:
                  "Internal audits, management review, training, KPI tracking",
              },
            ],
          })}
        </script>

        {/* Service Schema - Traceability Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "End-to-End Product Traceability",
            description:
              "Batch-level traceability system linking finished products to farmers, suppliers, and processing stages",
            provider: {
              "@type": "Organization",
              name: "Worga Naturals",
            },
            serviceType: "Supply Chain Traceability",
            areaServed: "Worldwide",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Traceability Features",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Origin Verification",
                    description: "Verify farm group and source locations",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Certification Status",
                    description: "Confirm organic and fair trade certification",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Processing Records",
                    description: "Access compliance checkpoints and CCPs",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Storage Conditions",
                    description: "Track storage and transport conditions",
                  },
                },
              ],
            },
          })}
        </script>

        {/* Dataset Schema - Sustainability Metrics */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: "Worga Naturals Sustainability Impact Metrics",
            description:
              "Measurable environmental and social impact data from quality and sustainability programs",
            creator: {
              "@type": "Organization",
              name: "Worga Naturals",
            },
            distribution: [
              {
                "@type": "DataDownload",
                encodingFormat: "application/pdf",
                contentUrl:
                  "https://worganaturals.com/reports/sustainability-report.pdf",
              },
            ],
            measurementTechnique: [
              "Batch-level traceability",
              "Third-party certification audits",
              "Laboratory testing",
              "Field monitoring",
            ],
            variableMeasured: [
              "Transparent traceability for every batch",
              "Certified organic and fair-trade supply chains",
              "Reduced chemical inputs and improved soil health",
              "Community investments through fair trade premiums",
              "Women's empowerment and leadership roles",
            ],
          })}
        </script>

        {/* WebPage Schema - Quality Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Quality & Sustainability - Worga Naturals",
            description:
              "Comprehensive quality management and sustainability practices with full traceability",
            url: "https://worganaturals.com/quality-sustainability",
            mainEntity: {
              "@type": "Thing",
              name: "Quality Assurance and Sustainability Framework",
              description:
                "BRC certified facility with HACCP, multiple organic certifications, and regenerative agriculture practices",
            },
            about: [
              {
                "@type": "Thing",
                name: "Traceability",
                description: "End-to-end visibility from farm to shelf",
              },
              {
                "@type": "Thing",
                name: "Quality Management",
                description: "HACCP, GMP, and BRC compliance",
              },
              {
                "@type": "Thing",
                name: "Sustainability",
                description: "Regenerative agriculture and community impact",
              },
              {
                "@type": "Thing",
                name: "Certifications",
                description:
                  "BRC, EU Organic, USDA, JAS, Naturland, Fairtrade, FDA",
              },
            ],
          })}
        </script>

        {/* Action Schema - Verification CTA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Action",
            name: "Verify Product Traceability",
            description:
              "Enter your product batch code to verify origin, certifications, and processing records",
            target: {
              "@type": "EntryPoint",
              urlTemplate:
                "https://worganaturals.com/verify-traceability?batch={batch_code}",
              actionPlatform: [
                "http://schema.org/DesktopWebPlatform",
                "http://schema.org/MobileWebPlatform",
              ],
            },
            result: {
              "@type": "Thing",
              name: "Traceability Report",
              description:
                "Complete chain of custody from farm to finished product",
            },
          })}
        </script>

        {/* ImageObject Schema - Certification Badges */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageObject",
            contentUrl:
              "https://worganaturals.com/images/certifications/all-certifications.jpg",
            name: "Worga Naturals Certifications",
            description:
              "BRC, EU Organic, USDA, JAS, Naturland, Fairtrade, and FDA certification badges",
            author: {
              "@type": "Organization",
              name: "Worga Naturals",
            },
          })}
        </script>

        {/* Video Schema - Quality Process (if you have video) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Worga Naturals Quality & Traceability System",
            description:
              "See how our end-to-end traceability system ensures quality from farm to shelf",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/quality-system.jpg",
            uploadDate: "2024-03-01",
            duration: "PT4M15S",
            contentUrl:
              "https://worganaturals.com/videos/quality-traceability.mp4",
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Spacer for navbar */}
        <div className="h-4"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-green-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("qualitySustainability.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("qualitySustainability.hero.title.part1")}
              </span>
              <span className="text-green-600 dark:text-green-500">
                {t("qualitySustainability.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
              {t("qualitySustainability.hero.description")}
            </p>
          </motion.div>
        </section>

        {/* Traceability Section */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                {t("qualitySustainability.traceability.title")}
              </h2>
              <p className="text-xl text-green-600 dark:text-green-400 font-semibold">
                {t("qualitySustainability.traceability.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {traceabilitySteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-green-500"
                >
                  <div className="text-4xl text-green-600 mb-4">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {t(
                      `qualitySustainability.traceability.steps.${index}.title`
                    )}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(
                      `qualitySustainability.traceability.steps.${index}.desc`
                    )}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* What You Can Verify */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 md:p-12 text-white shadow-2xl mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-6">
                {t("qualitySustainability.traceability.verify.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {t("qualitySustainability.traceability.verify.items", {
                  returnObjects: true,
                }).map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <FaCheckCircle className="text-green-200 text-xl mt-1 flex-shrink-0" />
                    <span className="text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Why It Matters */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-xl">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {t("qualitySustainability.traceability.whyMatters.title")}
              </h3>
              <ul className="space-y-4">
                {t("qualitySustainability.traceability.whyMatters.items", {
                  returnObjects: true,
                }).map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <span className="text-green-600 text-2xl">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Quality Management System */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("qualitySustainability.qualityManagement.title")}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {t("qualitySustainability.qualityManagement.items", {
                  returnObjects: true,
                }).map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-green-50 dark:bg-gray-700 rounded-xl p-6 border-l-4 border-green-600"
                  >
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sustainability in Practice */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("qualitySustainability.sustainability.title")}
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Regenerative Agriculture */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
              >
                <FaSeedling className="text-5xl text-green-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t(
                    "qualitySustainability.sustainability.regenerativeAgriculture.title"
                  )}
                </h3>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  {t(
                    "qualitySustainability.sustainability.regenerativeAgriculture.items",
                    { returnObjects: true }
                  ).map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span>
                        <strong>{item.label}</strong> {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Climate and Resources */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
              >
                <FaRecycle className="text-5xl text-blue-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t(
                    "qualitySustainability.sustainability.climateResources.title"
                  )}
                </h3>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  {t(
                    "qualitySustainability.sustainability.climateResources.items",
                    { returnObjects: true }
                  ).map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Community Impact */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
              >
                <FaUsers className="text-5xl text-purple-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t(
                    "qualitySustainability.sustainability.communityImpact.title"
                  )}
                </h3>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  {t(
                    "qualitySustainability.sustainability.communityImpact.items",
                    { returnObjects: true }
                  ).map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* Our Certifications */}
        <section className="bg-gradient-to-br from-gray-50 to-green-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("qualitySustainability.certifications.title")}
              </h2>

              <div className="space-y-8">
                {t("qualitySustainability.certifications.list", {
                  returnObjects: true,
                }).map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-10 hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="flex items-start space-x-6">
                      {/* Logo Image */}
                      <div className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                        <img
                          src={certificationLogos[index]}
                          alt={`${cert.name} logo`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                          {cert.name}
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">
                              {t(
                                "qualitySustainability.certifications.labels.whatItIs"
                              )}
                            </h4>
                            <p className="text-gray-700 dark:text-gray-300">
                              {cert.what}
                            </p>
                          </div>

                          <div>
                            <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">
                              {t(
                                "qualitySustainability.certifications.labels.whatItCovers"
                              )}
                            </h4>
                            <p className="text-gray-700 dark:text-gray-300">
                              {cert.covers}
                            </p>
                          </div>

                          <div>
                            <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">
                              {t(
                                "qualitySustainability.certifications.labels.whyItMatters"
                              )}
                            </h4>
                            <p className="text-gray-700 dark:text-gray-300">
                              {cert.why}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Compliance and Labeling */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              {t("qualitySustainability.compliance.title")}
            </h2>
            <ul className="space-y-4">
              {t("qualitySustainability.compliance.items", {
                returnObjects: true,
              }).map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <FaCheckCircle className="text-green-600 text-xl mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-700 dark:text-gray-300">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Testing and Verification */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <FaMicroscope className="text-6xl text-green-600 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("qualitySustainability.testing.title")}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {t("qualitySustainability.testing.items", {
                  returnObjects: true,
                }).map((item, i) => (
                  <div
                    key={i}
                    className="bg-green-50 dark:bg-gray-700 rounded-xl p-6 border-l-4 border-green-600"
                  >
                    <p className="text-gray-700 dark:text-gray-300 font-medium">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Food Safety Culture */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              {t("qualitySustainability.foodSafety.title")}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {t("qualitySustainability.foodSafety.items", {
                returnObjects: true,
              }).map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <FaCheckCircle className="text-blue-200 text-xl mt-1 flex-shrink-0" />
                  <span className="text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact You Can See */}
        <section className="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto text-center text-white"
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-12">
                {t("qualitySustainability.impact.title")}
              </h2>
              <div className="flex flex-wrap justify-center gap-8">
                {t("qualitySustainability.impact.stats", {
                  returnObjects: true,
                }).map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]"
                  >
                    <div className="text-5xl mb-4">{impactIcons[index]}</div>
                    <p className="text-lg font-semibold">{stat}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default QualitySustainability;
